import classNames from 'classnames';
import React, { useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import HubspotFormItem from '@/components/modules/hubspot-form/hubspot-form-item/hubspot-form-item';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { usePredefinedSearchContext } from '@/libraries/contexts/predefined-search-context/predefined-search-context';
import {
  COLUMN_LIMIT,
  HubspotFormData,
  LOADING_ROW_LIMIT,
  RoomMatrixProps,
  ROW_LIMIT,
} from '../room-matrix';
import RoomMatrixDate from '../room-matrix-date/room-matrix-date';
import RoomMatrixHeadline from '../room-matrix-headline/room-matrix-headline';
import RoomMatrixNavigation from '../room-matrix-navigation/room-matrix-navigation';
import RoomMatrixPrice from '../room-matrix-price/room-matrix-price';
import RoomMatrixRoom from '../room-matrix-room/room-matrix-room';
import styles from './room-matrix-small.module.scss';

interface RoomMatrixSmallProps extends RoomMatrixProps {
  /** Toggles collapsed view of the rooms */
  isCollapsed: boolean;
  /** The change handler */
  onNext: () => void;
  /** The change handler */
  onPrevious: () => void;
  /** The room type filter to show below the headline */
  roomTypeFilter?: React.ReactNode;
  /** The index of the first column to show */
  startIndex: number;
}

const RoomMatrixSmall = ({
  enableHubspotform = false,
  facilitiesInRoom = [],
  headline,
  isInConfigurationFlow,
  isCollapsed,
  isLoading,
  link,
  onNext,
  onPrevious,
  roomMatrix,
  roomTypeFilter,
  startIndex,
}: RoomMatrixSmallProps) => {
  const { t } = useI18n();
  const { searchSettings, setBookingUrl, setCurrentStepSummary, setPrice } =
    useHotelContext();
  const [formData, setFormData] = useState<HubspotFormData>();
  const { hasInPageNavigation } = usePageDetailsContext();
  const { offerId, setOfferId } = usePredefinedSearchContext();
  const singleColumnDate = roomMatrix?.singleColumnMode
    ? roomMatrix.columns[startIndex]
    : undefined;
  const hubspotForm =
    (searchSettings as Content.Components['SearchSettingsDto'])?.hubspotForm ??
    undefined;

  if (isLoading) {
    return (
      <div className={classNames(styles.component, styles.isLoading)}>
        <RoomMatrixHeadline headline="" isLoading link={null} tags={[]} />
        <RoomMatrixNavigation
          className={styles.navigation}
          isLoading
          onNext={onNext}
          onPrevious={onPrevious}
        />

        {Array.from({ length: LOADING_ROW_LIMIT }).map((_, rowIndex) => (
          <div key={rowIndex} className={styles.loadingRow}>
            <Typography tagStyle="bodySmall">
              {t('items.modules.roomMatrix.option', {
                number: rowIndex + 1,
              })}
            </Typography>

            <div className={styles.loadingOffers}>
              {Array.from({ length: COLUMN_LIMIT }).map((_, columnIndex) => (
                <RoomMatrixPrice
                  key={columnIndex}
                  discount={null}
                  isLoading
                  normalPrice={null}
                  totalPrice={null}
                />
              ))}
            </div>

            <div className={styles.loadingRooms}>
              {Array.from({ length: 1 }).map((_, roomIndex) => (
                <RoomMatrixRoom
                  key={roomIndex}
                  adultBeds={0}
                  childrenBeds={0}
                  description={null}
                  extraBeds={0}
                  facilitiesInRoom={[]}
                  isLoading
                  name=""
                  personsNo={0}
                  pictures={[]}
                  singleRoom
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className={styles.component}>
        <RoomMatrixHeadline
          className={styles.headline}
          headline={headline || ''}
          link={link}
          tags={facilitiesInRoom}
        />

        {roomTypeFilter && (
          <div className={styles.filter}>{roomTypeFilter}</div>
        )}

        <div
          className={classNames(styles.wrapper, {
            [styles.hasInPageNavigation]: hasInPageNavigation,
          })}
        >
          {singleColumnDate ? (
            <div
              className={classNames(styles.singleColumnDateContainer, {
                [styles.isCharter]: singleColumnDate.isCharter,
              })}
            >
              <RoomMatrixDate
                date={singleColumnDate.departureDate}
                isCharter={singleColumnDate.isCharter}
                weekNumber={singleColumnDate.weekNumber}
              />
            </div>
          ) : (
            <RoomMatrixNavigation
              className={styles.navigation}
              columns={roomMatrix?.columns}
              onNext={onNext}
              onPrevious={onPrevious}
              startIndex={startIndex}
            />
          )}
        </div>

        {roomMatrix &&
          roomMatrix.rows
            .slice(0, isCollapsed ? undefined : ROW_LIMIT)
            .map((row, rowIndex) => (
              <div key={rowIndex} className={styles.row}>
                <Typography tagStyle="bodySmall">
                  {t('items.modules.roomMatrix.option', {
                    number: rowIndex + 1,
                  })}
                </Typography>

                <div
                  className={classNames(styles.offers, {
                    [styles.isSingleColumnMode]: roomMatrix.singleColumnMode,
                  })}
                >
                  {row.cells
                    .slice(startIndex, startIndex + COLUMN_LIMIT)
                    .map((cell, cellIndex) => (
                      <RoomMatrixPrice
                        {...cell}
                        key={cellIndex}
                        additionalTag={cell.additionalTag ?? undefined}
                        enableHubspotform={enableHubspotform}
                        hubspotInfo={{
                          ...roomMatrix.enquireFormDataDto,
                          departureDate: cell.departureDate,
                        }}
                        isActive={
                          isInConfigurationFlow
                            ? offerId === cell.offerId
                            : cell.isSelected
                        }
                        onClickInquiry={value => setFormData(value)}
                        {...(isInConfigurationFlow && {
                          onClick: event => {
                            event?.preventDefault();
                            event?.stopPropagation();

                            if (!cell.offerId || !cell.bookingUrl) return;

                            setOfferId(cell.offerId);
                            setBookingUrl(cell.bookingUrl ?? undefined);
                            setCurrentStepSummary(row.rooms[0]?.name ?? '');
                            setPrice(cell.totalPrice ?? undefined);
                          },
                        })}
                      />
                    ))}
                </div>

                <div className={styles.rooms}>
                  {row.rooms.map((room, roomIndex) => (
                    <RoomMatrixRoom
                      {...room}
                      key={roomIndex}
                      className={styles.room}
                      singleRoom={row.rooms.length === 1}
                    />
                  ))}
                </div>
              </div>
            ))}
      </div>

      <Lightbox isOpen={!!formData} onClose={() => setFormData(undefined)}>
        {hubspotForm && (
          <HubspotFormItem
            key={JSON.stringify(formData)}
            form={hubspotForm}
            information={JSON.stringify(formData)}
            isLightbox
          />
        )}
      </Lightbox>
    </React.Fragment>
  );
};

RoomMatrixSmall.displayName = 'RoomMatrixSmall';

export default RoomMatrixSmall;
